import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import axios from 'axios';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';

export function LocationForm({ open, row, onClose, showMessage, ...props }) {
  const rows = Array.isArray(row) ? row : row ? [row] : [];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState({});

  const requestAccess = () => {
    setLoading(true);
    setError(null);
    axios.post('/api/wetlab/sample-locations/access-request', {
      ids: rows.map(sample => sample.id),
    })
      .then(({data}) => setLocations(data))
      .catch((error) => setError(error.response?.data?.error || error.message || 'An unknown error occurred'))
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setError(null);
    setLocations({});
    onClose?.();
  }

  const downloadTsv = () => {
    const escapeCell = value => String(value ?? '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
    const lines = [
      ['Sample', 'Owner', 'Box', 'Position in box', 'Location of box'],
      ...rows.map(sample => [
        sample.name || '',
        sample.owner?.username || '',
        locations?.[sample.id]?.box || '',
        locations?.[sample.id]?.position || '',
        locations?.[sample.id]?.box_location || '',
      ]),
    ].map(line => line.map(escapeCell).join('\t'));
    const url = URL.createObjectURL(new Blob([lines.join('\n')], {type: 'text/tab-separated-values;charset=utf-8'}));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample-locations.tsv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={!!open} onClose={handleClose}>
      <DialogTitle>Location of Sample{rows.length > 1 ? 's' : ''}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          If this is DNA (including plasmids and oligos), help yourself to a tiny bit for PCR and put it back in the exact same spot. For other kinds of samples, ideally, please check with me first. Either way, access to location information will be granted immediately.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sample</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Box</TableCell>
                <TableCell>Position</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(sample => (
                <TableRow key={sample.id}>
                  <TableCell>{sample.name}</TableCell>
                  <TableCell>{sample.owner?.username || 'Unknown'}</TableCell>
                  <TableCell>{locations?.[sample.id] ? (
                    <>
                      {locations[sample.id].box}
                      {locations[sample.id].box_location && <Tooltip title={locations[sample.id].box_location}>
                        <InfoIcon sx={{fontSize: 16, marginLeft: 1, verticalAlign: 'middle'}} />
                      </Tooltip>}
                    </>
                  ) : <LockIcon fontSize='small' />}</TableCell>
                  <TableCell>{locations?.[sample.id] ? locations[sample.id].position : <LockIcon fontSize='small' />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button
          variant="contained"
          onClick={requestAccess}
          disabled={loading || rows.length === 0}
        >
          {loading ? <CircularProgress size={20} /> : 'Request access'}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={downloadTsv} disabled={rows.length === 0}>Download TSV</Button>
        <Button onClick={handleClose} disabled={loading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export const LOCATIONS_FORM_MENUACTION_DEF = {
  label: 'Location',
  icon: <LocationOn />,
  Form: LocationForm,
  multiple: true,
};
